import React from 'react';
import { Skeleton, SimpleGrid, Paper, Stack, Group, Box } from '@mantine/core';

/**
 * DashboardSkeleton Props
 */
export interface DashboardSkeletonProps {
  /**
   * Number of stat cards to display
   * @default 4
   */
  statCards?: number;

  /**
   * Number of chart/widget cards to display
   * @default 2
   */
  widgetCards?: number;
}

/**
 * DashboardSkeleton - Loading placeholder for dashboard pages
 *
 * Features:
 * - Stat cards skeleton (top row)
 * - Widget/chart cards skeleton (bottom section)
 * - Configurable number of cards
 * - Responsive grid layout
 * - Animates while loading
 */
export const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({
  statCards = 4,
  widgetCards = 2
}): React.ReactElement => {
  return (
    <Stack gap="lg">
      {/* Stat Cards Row */}
      <SimpleGrid
        cols={{ base: 1, xs: 2, md: statCards }}
        spacing="md"
      >
        {Array.from({ length: statCards }).map((_, index) => (
          <Paper key={`stat-${index}`} shadow="sm" p="md" radius="md" withBorder>
            <Stack gap="sm">
              {/* Stat Label */}
              <Skeleton height={14} width="60%" />

              {/* Stat Value */}
              <Group justify="apart" align="flex-end">
                <Skeleton height={32} width="40%" />
                <Skeleton height={20} width="30%" />
              </Group>

              {/* Stat Trend/Icon */}
              <Skeleton height={12} width="50%" />
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Widget Cards Section */}
      <SimpleGrid
        cols={{ base: 1, md: widgetCards }}
        spacing="md"
      >
        {Array.from({ length: widgetCards }).map((_, index) => (
          <Paper key={`widget-${index}`} shadow="sm" p="md" radius="md" withBorder>
            <Stack gap="md">
              {/* Widget Title */}
              <Group justify="apart">
                <Skeleton height={20} width="40%" />
                <Skeleton height={28} width={80} radius="sm" />
              </Group>

              {/* Widget Content (chart/table placeholder) */}
              <Box>
                <Skeleton height={200} width="100%" />
              </Box>

              {/* Widget Footer */}
              <Group justify="apart">
                <Skeleton height={12} width="30%" />
                <Skeleton height={12} width="25%" />
              </Group>
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>
    </Stack>
  );
};
